import { ArrowLeft } from "lucide-react";
import posthog from "posthog-js";
import { useState } from "react";

import EvaluationPreset, { EvaluationPresetStep } from "./EvaluationPreset";
import PresetGrid from "./PresetGrid";
import { dedupeModelLabels, ModelComparisonSelection } from "./modelComparisonTypes";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  EvaluationPresetType,
  useCreateCustomPreset,
  useCreateEvaluationPreset,
  useCreateModelComparisonPreset,
} from "@/queries/smart-tables";
import { PromptVersion } from "@/types/apiGetters";
import { displayErrorToast } from "@/utils/toast";

interface PromptTemplatePresetEvaluationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promptRegistryId: number;
  promptVersionNumber: number;
  folderId?: number | null;
  promptName?: string;
  promptVersion?: PromptVersion;
}

type PresetDialogStep = "presets" | EvaluationPresetStep;

const stepTitle = (step: PresetDialogStep) => {
  if (step === "model-comparison") {
    return "Model comparison";
  }
  if (step === "GROUND_TRUTH_COMPARE") {
    return "Ground truth check";
  }
  if (step === "STRUCTURAL") {
    return "Structure check";
  }
  if (step === "custom") {
    return "Custom";
  }
  return "Evaluate prompt";
};

const stepDescription = (step: PresetDialogStep) => {
  if (step === "model-comparison") {
    return "Run one prompt across multiple models and compare output, cost, and latency.";
  }
  if (step === "GROUND_TRUTH_COMPARE") {
    return "Compare generated output against a trusted answer with focused pass/fail checks.";
  }
  if (step === "STRUCTURAL") {
    return "Verify that outputs follow your required format using clear structure checks.";
  }
  if (step === "custom") {
    return "Create input columns and one prompt output column connected to those variables.";
  }
  return "Choose the closest evaluation preset. You can edit the table after it is created.";
};

const customTableTitle = (promptName?: string) =>
  promptName ? `${promptName} custom evaluation` : "Custom prompt evaluation";

const evaluationTableTitle = (presetType: EvaluationPresetType, promptName?: string) => {
  const suffix = presetType === "GROUND_TRUTH_COMPARE" ? "ground truth check" : "structure check";
  if (!promptName) {
    return presetType === "GROUND_TRUTH_COMPARE" ? "Ground truth check" : "Structure check";
  }
  return `${promptName} ${suffix}`;
};

const PromptTemplatePresetEvaluationsDialog = ({
  open,
  onOpenChange,
  promptRegistryId,
  promptVersionNumber,
  folderId,
  promptName,
  promptVersion,
}: PromptTemplatePresetEvaluationsDialogProps) => {
  const [step, setStep] = useState<PresetDialogStep>("presets");
  const createPreset = useCreateModelComparisonPreset();
  const createEvaluationPreset = useCreateEvaluationPreset();
  const createCustomPreset = useCreateCustomPreset();
  const modelComparisonTableTitle = promptName
    ? `${promptName} model comparison`
    : "Model comparison";
  const isPresets = step === "presets";
  const activePresetStep: EvaluationPresetStep | null = step === "presets" ? null : step;

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setStep("presets");
    }
  };

  const handleSelectPreset = (nextStep: EvaluationPresetStep) => {
    posthog.capture("Smart Table Preset Selected", {
      preset_type: nextStep,
      source: "prompt-template-dialog",
    });
    setStep(nextStep);
  };

  const handleSubmit = (
    selections: ModelComparisonSelection[],
    initialInputs: Record<string, string>,
  ) => {
    const normalizedModels = dedupeModelLabels(
      selections.filter(selection => Boolean(selection.engine.model?.trim())),
    );

    if (normalizedModels.length < 2) {
      return;
    }

    createPreset.mutate(
      {
        prompt_registry_id: promptRegistryId,
        prompt_version_number: promptVersionNumber,
        title: modelComparisonTableTitle,
        folder_id: folderId ?? null,
        models: normalizedModels,
        initial_inputs: initialInputs,
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
        },
        onError: error => {
          displayErrorToast(error);
        },
      },
    );
  };

  const handleCustomSubmit = (initialInputs: Record<string, string>) => {
    createCustomPreset.mutate(
      {
        prompt_registry_id: promptRegistryId,
        prompt_version_number: promptVersionNumber,
        title: customTableTitle(promptName),
        folder_id: folderId ?? null,
        initial_inputs: initialInputs,
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
        },
        onError: error => {
          displayErrorToast(error);
        },
      },
    );
  };

  const handleEvaluationSubmit = ({
    presetType,
    initialInputs,
    expectedValue,
    assertions,
  }: {
    presetType: EvaluationPresetType;
    initialInputs: Record<string, string>;
    expectedValue?: string;
    assertions: string[];
  }) => {
    const tableTitle = evaluationTableTitle(presetType, promptName);

    createEvaluationPreset.mutate(
      {
        prompt_registry_id: promptRegistryId,
        prompt_version_number: promptVersionNumber,
        preset_type: presetType,
        title: tableTitle,
        folder_id: folderId ?? null,
        initial_inputs: initialInputs,
        expected_value: expectedValue,
        assertions,
      },
      {
        onSuccess: () => {
          handleOpenChange(false);
        },
        onError: error => {
          displayErrorToast(error);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[86vh] w-[calc(100vw-1rem)] max-w-2xl gap-0 overflow-hidden border-gray-200/80 bg-white/95 p-0 shadow-2xl shadow-gray-950/10 backdrop-blur sm:w-[calc(100vw-2rem)]">
        <div className="border-b border-gray-100/80 bg-white px-4 py-4 sm:px-5">
          <DialogHeader className="space-y-1.5 text-left">
            <div className="flex min-h-8 items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                {!isPresets && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 rounded-full bg-white/70 text-gray-500 shadow-sm ring-1 ring-gray-200/70 backdrop-blur transition hover:bg-white hover:text-gray-950 hover:shadow-md"
                    onClick={() => setStep("presets")}
                    aria-label="Back to presets"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                )}
                <DialogTitle className="truncate text-base font-semibold leading-6 tracking-[-0.01em] text-gray-950">
                  {stepTitle(step)}
                </DialogTitle>
              </div>
            </div>
            <DialogDescription className="max-w-xl text-sm leading-5 text-gray-600">
              {stepDescription(step)}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="overflow-hidden bg-gradient-to-b from-white via-white to-gray-50/70">
          <div
            className={`grid transition-[grid-template-rows,opacity,transform] duration-200 ease-out ${
              !isPresets
                ? "pointer-events-none -translate-y-1 grid-rows-[0fr] opacity-0"
                : "translate-y-0 grid-rows-[1fr] opacity-100"
            }`}
          >
            <div className="min-h-0 overflow-hidden">
              <PresetGrid
                onSelectModelComparison={() => handleSelectPreset("model-comparison")}
                onSelectGroundTruth={() => handleSelectPreset("GROUND_TRUTH_COMPARE")}
                onSelectStructural={() => handleSelectPreset("STRUCTURAL")}
                onSelectCustom={() => handleSelectPreset("custom")}
              />
            </div>
          </div>

          <div
            className={`grid transition-[grid-template-rows,opacity,transform] duration-200 ease-out ${
              !isPresets
                ? "translate-y-0 grid-rows-[1fr] opacity-100"
                : "pointer-events-none translate-y-1 grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="min-h-0 overflow-hidden">
              <EvaluationPreset
                step={activePresetStep}
                promptVersion={promptVersion}
                isSubmittingModelComparison={createPreset.isPending}
                isSubmittingCustom={createCustomPreset.isPending}
                isSubmittingEvaluation={createEvaluationPreset.isPending}
                onSubmitModelComparison={handleSubmit}
                onSubmitCustom={handleCustomSubmit}
                onSubmitEvaluation={handleEvaluationSubmit}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PromptTemplatePresetEvaluationsDialog;
